/* HealthIntelligenceScoreRing — gamified 0-1000 точки число (iter420.2).
 *
 * Whoop-style психологически hook — потребителят се връща ежедневно за да
 * види как растат точките му. Conic-gradient ring + breakdown chip + next
 * step hint която предлага най-доходоносната следваща стъпка.
 *
 * Props:
 *   score: { value, max, band, breakdown, next_step_hint }
 *   isDark, bg
 */
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, TrendingUp } from "lucide-react";

const BAND_META = {
  new:      { bg: "Новак",     en: "New",      color: "#94A3B8", grad: "from-slate-400 to-slate-500" },
  starter:  { bg: "Начинаещ",  en: "Starter",  color: "#0EA5E9", grad: "from-sky-400 to-cyan-500" },
  building: { bg: "Изграждаш", en: "Building", color: "#8B5CF6", grad: "from-violet-500 to-fuchsia-500" },
  advanced: { bg: "Напреднал", en: "Advanced", color: "#10B981", grad: "from-emerald-500 to-teal-500" },
  elite:    { bg: "Elite",     en: "Elite",    color: "#F59E0B", grad: "from-amber-400 to-orange-500" },
};

export default function HealthIntelligenceScoreRing({ score, isDark, bg }) {
  if (!score) return null;
  const value = Number(score.value || 0);
  const max = Number(score.max || 1000);
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const band = BAND_META[score.band] || BAND_META.new;
  const breakdown = score.breakdown || {};

  return (
    <Card className={`border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}
          data-testid="hi-score-ring">
      <CardContent className="p-5">
        <div className="flex items-center gap-5 flex-wrap sm:flex-nowrap">
          {/* Ring (conic-gradient) */}
          <div className="relative w-32 h-32 flex-shrink-0">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(${band.color} ${pct * 3.6}deg, ${isDark ? "#1E293B" : "#E2E8F0"} 0deg)`,
              }}
            />
            <div className={`absolute inset-2.5 rounded-full flex flex-col items-center justify-center ${isDark ? "bg-slate-900" : "bg-white"}`}>
              <div className={`text-3xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}
                   data-testid="hi-score-value">{value}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">/ {max}</div>
            </div>
          </div>

          {/* Right side — band + breakdown */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Brain className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
              <span className={`text-[11px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Health Intelligence Score" : "Health Intelligence Score"}
              </span>
              <span className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase text-white bg-gradient-to-r ${band.grad}`}
                    data-testid="hi-score-band">
                {bg ? band.bg : band.en}
              </span>
            </div>

            {/* Breakdown bars */}
            <div className="space-y-1.5">
              {[
                ["profile_completeness", bg ? "Профил" : "Profile", 200],
                ["activity",            bg ? "Активност" : "Activity", 300],
                ["recency_7d",          bg ? "Скоро (7д)" : "Recent (7d)", 200],
                ["diversity",           bg ? "Разнообразие" : "Diversity", 200],
                ["data_depth",          bg ? "Дълбочина" : "Depth", 100],
              ].map(([key, label, maxBar]) => {
                const v = breakdown[key] || 0;
                const w = (v / maxBar) * 100;
                return (
                  <div key={key} className="flex items-center gap-2"
                       data-testid={`hi-score-breakdown-${key}`}>
                    <span className={`text-[10.5px] font-medium w-24 flex-shrink-0 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      {label}
                    </span>
                    <div className={`flex-1 h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
                      <div className={`h-full bg-gradient-to-r ${band.grad} transition-all`}
                           style={{ width: `${w}%` }} />
                    </div>
                    <span className={`text-[10.5px] font-bold w-14 text-right flex-shrink-0 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {v}/{maxBar}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Next step hint */}
            {score.next_step_hint && (
              <div className={`mt-3 flex items-start gap-2 p-2.5 rounded-lg ${
                    isDark ? "bg-violet-500/10 border border-violet-500/20" : "bg-violet-50 border border-violet-200"
                  }`} data-testid="hi-score-hint">
                <TrendingUp className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                <p className={`text-[11px] leading-relaxed ${isDark ? "text-violet-200" : "text-violet-900"}`}>
                  <strong>{bg ? "Следваща стъпка:" : "Next step:"}</strong> {score.next_step_hint}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
